import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getUsers, createAnyUser, deleteAnyUser, updateUserBillingAmount } from '../services/api';
import { useTranslation } from 'react-i18next';
import BillingModal from './modals/BillingModal';

interface UserManagementProps {
  clientId?: string;
}

const UserManagement: React.FC<UserManagementProps> = ({ clientId = '1' }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [userToChange, setUserToChange] = useState<{id: number, billing_amount: number} | null>(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'admin', client_id: '', client_name: '', email: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMountedRef = useRef(false);

  const fetchUsers = useCallback(async () => {
    try {
      const fetchedUsers = await getUsers();
      if (!isMountedRef.current) return;
      if (Array.isArray(fetchedUsers)) {
        setUsers(fetchedUsers);
      } else {
        console.error('Fetched users is not an array:', fetchedUsers);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      if (!isMountedRef.current) return;
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchUsers();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchUsers]);

  const handleAccept = async (billing_amount: number) => {
    setIsModalOpen(false);
    const updatedUser = await updateUserBillingAmount(userToChange.id, billing_amount);
    if (!isMountedRef.current) return;

    setUsers((prevUsers) =>
      prevUsers.map(user =>
        user.id === updatedUser.id
          ? { ...user, billing_amount: updatedUser.billing_amount }
          : user
      )
    );

    setUserToChange(null);
  };

  const handleCreateUser = async () => {
    const userData: any = {
      username: newUser.username,
      password: newUser.password,
      role: newUser.role
    };

    if (newUser.role === 'user') {
      userData.client_id = newUser.client_id;
    }

    if (newUser.role === 'client') {
      userData.client_name = newUser.client_name;
      userData.email = newUser.email;
    }

    await createAnyUser(userData);
    if (!isMountedRef.current) return;
    setNewUser({ username: '', password: '', role: 'admin', client_id: '', client_name: '', email: '' });
    fetchUsers();
  };

  const handleDeleteUser = async (userId: string, role: string) => {
    if (window.confirm(t('userManagement.confirmDelete'))) {
      await deleteAnyUser(userId, role);
      if (!isMountedRef.current) return;
      fetchUsers();
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'client':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const groupedUsers = Array.isArray(users) ? users.reduce((acc: any, user: any) => {
    if (user.role === 'admin') {
      if (!acc.admins) acc.admins = [];
      acc.admins.push(user);
    } else if (user.role === 'client') {
      const clientName = user.client_name || `Client ${user.client_id}`;
      if (!acc.clients[clientName]) {
        acc.clients[clientName] = {
          client: user,
          users: []
        };
      } else {
        acc.clients[clientName].client = user;
      }
    } else if (user.role === 'user') {
      const clientName = user.client_name || `Client ${user.client_id}`;
      if (!acc.clients[clientName]) {
        acc.clients[clientName] = {
          client: null,
          users: []
        };
      }
      acc.clients[clientName].users.push(user);
    }
    return acc;
  }, { admins: [], clients: {} }) : { admins: [], clients: {} };

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('userManagement.title')}</h2>
        <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2.5 py-0.5 rounded-full text-sm font-medium">
          {t('userManagement.userCount', { count: users.length })}
        </span>
      </div>

      <form
        className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6"
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
      >
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('userManagement.addNewUser')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('userManagement.username')}
            </label>
            <input
              type="text"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              className="input-field"
              placeholder={t('userManagement.enterUsername')}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('userManagement.password')}
            </label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              className="input-field"
              placeholder={t('userManagement.enterPassword')}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('userManagement.role')}
            </label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="input-field"
            >
              <option value="user">{t('userManagement.roles.user')}</option>
              <option value="client">{t('userManagement.roles.client')}</option>
              <option value="admin">{t('userManagement.roles.admin')}</option>
            </select>
          </div>
        </div>
        {newUser.role === 'user' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('userManagement.clientId')}
            </label>
            <input
              type="text"
              value={newUser.client_id}
              onChange={(e) => setNewUser({ ...newUser, client_id: e.target.value })}
              className="input-field"
              placeholder={t('userManagement.enterClientId')}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('userManagement.clientIdRequired')}
            </p>
          </div>
        )}
        {newUser.role === 'client' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Client Name
            </label>
            <input
              type="text"
              value={newUser.client_name}
              onChange={(e) => setNewUser({ ...newUser, client_name: e.target.value })}
              className="input-field"
              placeholder="Enter client name"
            />
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 mt-4">
              Email
            </label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="input-field"
              placeholder="Enter client email"
              autoComplete="off"
            />
          </div>
        )}
        <div className="mt-4">
            <button 
              type="button"
              onClick={handleCreateUser}
              className="btn-primary"
              disabled={
                !newUser.username ||
                !newUser.password ||
                (newUser.role === 'user' && !newUser.client_id) ||
                (newUser.role === 'client' && (!newUser.client_name || !newUser.email))
              }
            >
              {t('userManagement.addUser')}
            </button>
        </div>
      </form>

      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('userManagement.activeUsers')}</h3>
        {users.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <p className="mt-2">{t('userManagement.noUsers')}</p>
            <p className="text-sm">{t('userManagement.addFirstUser')}</p>
          </div>
        ) : (
          <>
            {groupedUsers.admins && groupedUsers.admins.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 border-b dark:border-gray-700 pb-2">{t('userManagement.admins')}</h4>
                {groupedUsers.admins.map((user: any) => (
                  <div key={user.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {user.username?.charAt(0)?.toUpperCase() || 'A'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{user.username}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                            {t(`userManagement.roles.${user.role}`)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDeleteUser(user.id, user.role)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
                      >
                        {t('userManagement.removeUser')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {Object.keys(groupedUsers.clients).sort().map((clientName) => {
              const clientData = groupedUsers.clients[clientName];
              const allUsers = [
                ...(clientData.client ? [clientData.client] : []),
                ...clientData.users
              ];

              return (
                <div key={clientName} className="space-y-3">
                  <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 border-b dark:border-gray-700 pb-2">{clientName}</h4>
                  {allUsers.map((user: any) => {
                    const avatarColor = user.role === 'client' ? 'bg-blue-500' : 'bg-green-500';
                    return (
                      <div key={user.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className={`w-10 h-10 ${avatarColor} rounded-full flex items-center justify-center`}>
                              <span className="text-white font-medium text-sm">
                                {user.username?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{user.username}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                                {t(`userManagement.roles.${user.role}`)}
                              </span>
                              {user.role === 'client' && user.client_id && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {user.client_id}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => {
                              setIsModalOpen(true);
                              setUserToChange(user);
                            }}
                            className="mr-5 border rounded-lg p-2 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-blue-50 dark:hover:bg-white-100"
                          >
                            {user.billing_amount || '0.00'}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.role)}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
                          >
                            {t('userManagement.removeUser')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}
      </div>
      {userToChange && (
        <BillingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAccept={(billing_amount: number) => handleAccept(billing_amount)}
          currentAmount={userToChange.billing_amount}
        />
      )}
    </div>
  );
};

export default UserManagement;